import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { categoriesApi, itemsApi, locationsApi } from '../api';
import { flattenTree } from '../lib/tree';
import { formatQuantity } from '../lib/format';
import { hasInventoryFields } from '../lib/categories';
import { canWrite } from '../lib/permissions';
import { useAuth } from '../context/AuthContext';

export default function ItemsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const writeAccess = canWrite(user?.role);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [locationId, setLocationId] = useState<number | ''>('');

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.list,
  });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['items', { categoryId, locationId, search }],
    queryFn: () =>
      itemsApi.list({
        categoryId: categoryId || undefined,
        locationId: locationId || undefined,
        search: search.trim() || undefined,
      }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => itemsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
  });

  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );
  const locationById = useMemo(
    () => new Map(locations.map((l) => [l.id, l])),
    [locations],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('items.title')}</h1>
          <p className="text-sm text-slate-600">{t('items.intro')}</p>
        </div>
        <button
          onClick={() => navigate('/items/new')}
          className="bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium"
        >
          {t('items.new')}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <input
          type="search"
          placeholder={t('items.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        />
        <div className="grid grid-cols-2 gap-3">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
            className="rounded border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">{t('items.allCategories')}</option>
            {flattenTree(categories).map((c) => (
              <option key={c.id} value={c.id}>
                {'— '.repeat(c.depth)}
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : '')}
            className="rounded border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">{t('items.allLocations')}</option>
            {flattenTree(locations).map((l) => (
              <option key={l.id} value={l.id}>
                {'— '.repeat(l.depth)}
                {l.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t('items.empty')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('field.name')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('field.category')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('field.location')}
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase">
                  {t('field.quantity')}
                </th>
                {writeAccess && (
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const category = item.categoryId ? categoryById.get(item.categoryId) : null;
                const location = item.locationId ? locationById.get(item.locationId) : null;
                const enabled = item.enabledFields ?? [];
                const isInventory =
                  enabled.includes('quantity') ||
                  hasInventoryFields(item.categoryId, categories);
                const qtyNum = Number(item.quantity);
                // Vis antal hvis lager-kategori ELLER hvis nogen eksplicit har sat antal > 1
                const showQuantity = isInventory || qtyNum !== 1;
                const lowStock =
                  item.lowStockThreshold &&
                  qtyNum <= Number(item.lowStockThreshold);
                return (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2">
                      <Link
                        to={`/items/${item.id}`}
                        className="font-medium text-slate-900 hover:underline"
                      >
                        {item.name}
                      </Link>
                      {item.description && (
                        <div className="text-xs text-slate-500 line-clamp-1">
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-600">
                      {category ? (
                        <span className="inline-flex items-center gap-1.5">
                          {category.color && (
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: category.color }}
                            />
                          )}
                          {category.name}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-600">{location?.path ?? '—'}</td>
                    <td className="px-4 py-2 text-right text-sm">
                      {showQuantity ? (
                        <>
                          <span
                            className={lowStock ? 'text-red-600 font-medium' : 'text-slate-700'}
                          >
                            {formatQuantity(item.quantity)} {item.unit}
                          </span>
                          {lowStock && <span className="ml-1 text-xs">⚠</span>}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    {writeAccess && (
                      <td className="px-4 py-2 text-right text-sm space-x-3 whitespace-nowrap">
                        <Link
                          to={`/items/${item.id}/edit`}
                          className="text-slate-600 hover:text-slate-900 hover:underline"
                        >
                          {t('common.edit')}
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm(t('items.confirmDelete', { name: item.name }))) {
                              removeMutation.mutate(item.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-800 hover:underline"
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

